import { cleanFullAddress, PropertyAddress } from '@urbanx/agx-ui-components'
import {
  AutopilotTaskDto,
  TaskCategoryTaskType,
  TaskCategoryWorkflowType,
  useInvalidateUseGetAutopilotTasksForCampaign,
  useWithdrawWorkflow,
  WorkflowTaskRequest,
} from '@urbanx/agx.activities.client'
import { AuthContext } from '@urbanx/agx.auth.client'
import { MenuTitles } from 'constants/menu'
import { useSelectedAgency, useSelectedCampaign } from 'hooks'
import { ContentLayout, PageLayout } from 'layout'
import { useCallback, useContext, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  AgxButton,
  AgxModal,
  AgxRow,
  Icon_AlertCircleOutline,
} from '@urbanx/agx.ui-components2'
import Tasks from 'components/tasks/Tasks'

const ContractTasks = () => {
  const { contractId } = useParams()
  const { userAccount } = useContext(AuthContext)
  const { agency } = useSelectedAgency()
  const { campaign } = useSelectedCampaign()

  const [showTerminateConfirmation, setShowTerminateConfirmation] =
    useState(false)
  const [allTasks, setAllTasks] = useState<AutopilotTaskDto[]>([])

  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign()

  const { withdrawWorkflow } = useWithdrawWorkflow({
    onSuccess: () => {
      invalidateTasks(campaign?.campaignId ?? '', contractId)
    },
    onError: () => {},
  })

  const onTerminate = () => {
    setShowTerminateConfirmation(true)
  }

  const onConfirmTerminate = () => {
    const request: WorkflowTaskRequest = {
      campaignId: campaign?.campaignId,
      contractId,
    }
    withdrawWorkflow(request)
    setShowTerminateConfirmation(false)
  }

  const terminatedTask = useMemo(() => {
    return allTasks.find(
      (t) => t.category?.taskType === TaskCategoryTaskType.Termination
    )
  }, [allTasks])

  const handleTasksChange = useCallback((tasks: AutopilotTaskDto[]) => {
    setAllTasks(tasks)
  }, [])

  // Get unlocked contract tasks to determine if terminate button should be disabled
  const unlockedContractTasks = useMemo(() => {
    return allTasks.filter(
      (t) =>
        !t.isLocked &&
        t.category?.workflowType === TaskCategoryWorkflowType.Contract
    )
  }, [allTasks])

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={agency?.name || ''}
      currentPageTitle={`Campaigns / ${cleanFullAddress(campaign?.address as PropertyAddress)}`}
      isCampaignsSection={true}
    >
      <ContentLayout
        hasSecondaryMenu={false}
        secondaryActiveMenu={MenuTitles.CONTRACT_TASKS}
        isContracts={true}
        secondaryActions={
          <AgxButton
            id={'btnTerminateContract'}
            text={'Terminate contract'}
            variant={'naked'}
            danger
            onClick={onTerminate}
            disabled={
              unlockedContractTasks.length === 0 ||
              (terminatedTask && !terminatedTask.isLocked)
            }
          />
        }
      >
        <Tasks
          campaignId={campaign?.campaignId ?? ''}
          contractId={contractId}
          primaryWorkflowType={TaskCategoryWorkflowType.Contract}
          onTasksChange={handleTasksChange}
        />

        <AgxModal
          title={'Mark as Terminated?'}
          titleIcon={
            <div className="modelTitleIconContainer">
              <Icon_AlertCircleOutline type={'Danger'} height={40} width={40} />
            </div>
          }
          type={'single'}
          variant={'withDivider'}
          actionButtonContainerStyle={'withDivider'}
          actionButtons={
            <AgxRow gap={'spacing-md'} justifyContent="center" fill>
              <AgxButton
                id={'btnCancel'}
                text={'Cancel'}
                variant={'hollow'}
                onClick={() => setShowTerminateConfirmation(false)}
                stretch
              />
              <AgxButton
                id={'btnConfirm'}
                text={'Confirm'}
                variant={'primary'}
                danger
                onClick={onConfirmTerminate}
                stretch
              />
            </AgxRow>
          }
          content={<></>}
          isOpen={showTerminateConfirmation}
          setIsDialogOpen={setShowTerminateConfirmation}
        />
      </ContentLayout>
    </PageLayout>
  )
}

export default ContractTasks
